import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Card, CardContent, CardHeader, Grow, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material';
import { Q } from '@nozbe/watermelondb'
import withObservables from '@nozbe/with-observables'
import _ from 'lodash'
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { withAfterSalesDatabase } from '../../../../model/aftersales/AfterSalesDatabase';
import { W_CONTROLLERS_0 } from '../../../../model/Hardware';
import SelectDeviceWithScanner from '../factory/select-with-scanner';
import { topicBuilder, useMqttListener } from '../../../devices';
import { useTranslation } from 'react-i18next';

export const useNavigateToDevice = () => {
    const navigate = useNavigate();
    const handleClick = useCallback((id) => {
        navigate(`${id}/details`);
    }, []);
    return handleClick;
}


export const useConnectedDevicesListener = () => {
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [devices, setDevices] = useState({});
    const listener = useCallback((data, params) => {
        setLastUpdate(new Date());
        setDevices(devices => ({
            ...devices,
            [params.serialNumber]: data,
        }))
    }, []);
    const topic = useMemo(() => topicBuilder(`v1/dev/+serialNumber/d/connection`), []);
    useMqttListener(topic, listener);
    return { devices, lastUpdate };
}

const DeviceField = ({ label, value, width }) => (
    <Box sx={{ width, minWidth: width, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" noWrap>{value || '-'}</Typography>
    </Box>
);

DeviceField.propTypes = {
    label: PropTypes.string,
    value: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export function DeviceListItem({ device, index }) {

    const handleClick = useNavigateToDevice();

    return (
        <Grow in>
            <ListItem disablePadding onClick={() => handleClick(device.id)}>
                <ListItemButton>
                    <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'flex-start' }}>
                        <DeviceField label="#" value={String(index + 1)} width={50} />
                        <DeviceField label="ID" value={device.id} width={280} />
                        <DeviceField label="Hardware" value={device.hardware} width={120} />
                        <DeviceField label="Status" value={device.factoryStatus} width={120} />
                        <DeviceField label="Last Connection" value={device.lastConnection ? dayjs(device.lastConnection).format('YYYY-MM-DD HH:mm') : null} width={150} />
                    </Box>
                </ListItemButton>
            </ListItem>
        </Grow>
    )
}

DeviceListItem.propTypes = {
    device: PropTypes.any,
    index: PropTypes.number
}

const observeDevice = withObservables(['device'], ({ device }) => ({
    device,
}));

export const ObservedDeviceListItem = withAfterSalesDatabase(observeDevice(DeviceListItem));

export function DevicesListInternal({ devices }) {
    return (
        <List>
            {_.map(devices, (device, index) => <ObservedDeviceListItem key={device.id} device={device} index={index} />)}
        </List>
    );
}

DevicesListInternal.propTypes = {
    devices: PropTypes.array
}

const observeOnlineDevices = withObservables(['onlineDevices'], ({ database, onlineDevices }) => ({
    devices: database.get('devices').query(
        Q.and(
            Q.where('id', Q.oneOf(onlineDevices)),
            Q.where('hardware', W_CONTROLLERS_0)
        ),
        Q.sortBy('last_connection', Q.desc))
}));

const observeAllDevices = withObservables([], ({ database }) => ({
    devices: database.get('devices').query(
        Q.and(Q.where('hardware', W_CONTROLLERS_0)),
        Q.sortBy('last_connection', Q.desc)),
}));

export const ObservedOnlineDevicesList = withAfterSalesDatabase(observeOnlineDevices(DevicesListInternal))
export const ObservedAllDevicesList = withAfterSalesDatabase(observeAllDevices(DevicesListInternal))

export default function DevicesList({ }) {

    const { t } = useTranslation();
    const handleClick = useNavigateToDevice();
    const { devices } = useConnectedDevicesListener();

    const onlineDevices = useMemo(() => _.compact(
        _.map(devices,
            (device, serialNumber) => device.state === 'connected' ? serialNumber : null
        )
    ), [devices]);

    return (
        <Stack spacing={2}>
            <Card>
                <CardHeader
                    title={t('DEVICES.ONLINE_LIST_HEADER')}
                />
                <CardContent>
                    <ObservedOnlineDevicesList onlineDevices={onlineDevices} />
                </CardContent>
            </Card>
            <Card>
                <CardHeader
                    title={t('DEVICES.ALL_LIST_HEADER')}
                    action={<SelectDeviceWithScanner onClick={handleClick} />}
                />
                <CardContent>
                    <ObservedAllDevicesList />
                </CardContent>
            </Card>
        </Stack>
    );
};

DevicesList.propTypes = {
    query: PropTypes.any,
}
